import React, { useEffect, useState } from "react";
import { Button } from "../../components/Shared/Buttons/Button";
import { TextInput } from "../Shared/Inputs/TextInput";
import { ReactComponent as CloseIcon } from '../../assets/svg/black_close-circle.svg';
import { ReactComponent as Duplicate } from '../../assets/svg/duplicate.svg';
import { LoggedRecruiter } from '../../models/LoggedRecruiter';
import { addOrganizationDomain, getOrganizationDomains, removeOrganizationDomain } from "../../services/RecruiterService";
import { ResponseModel } from "../../models/ResponseModel";
import { useAlert } from "../../hooks/useAlert";
import { WhitelistedDomain } from "../../models/WhitelistedDomain";
import useErrorPage from '../../hooks/useErrorPage';
import { IntegrationLoader } from "../Loaders/IntegrationLoader";
import { RecruiterDomainValidation } from "../../models/RecruiterAccountModel";
import { UserType } from "../../enums/UserType";

interface Props {
    styles?: any;
    recruiter: LoggedRecruiter;
};

const initialDomainValidation: RecruiterDomainValidation = {
    domain: {}
}

export const CareerPage: React.FC<Props> = (props) => {
    const styles = props.styles;
    const recruiter = props.recruiter;
    const { Alert, openAlert } = useAlert();
    const [whitelistedDomains, setWhitelistedDomains] = useState<WhitelistedDomain[]>([]);
    const [toolTipElement, setToolTipElement] = useState<string>();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [toErrorPage] = useErrorPage();
    const initionalDomain : WhitelistedDomain = {
        id: 0,
        domain : "",
        recruiterId: recruiter.recruiterId
    }
    const [domainDetail, setDomaiDetail] = useState<WhitelistedDomain>(initionalDomain);
    const [domainValidation, setDomainValidation] = useState<RecruiterDomainValidation>(initialDomainValidation);
    const AdditionalOptions = [
        { name: 'showType', value: 'true'}, 
        { name: 'showOverview', value: 'false'}, 
        { name: 'showLocations' ,value: 'true'}, 
        { name: 'showSearchBar', value: 'true'}, 
        { name: 'showHeading', value: 'true'}, 
        { name: 'heading', value: 'Crurrent Openings'}
    ];
    const embedCodeConfig: { [key: string]: { code: string; style: any } } = {
        css: {
            code: "<link rel=\"stylesheet\" href=\"https://xj-cdn.s3-ap-southeast-1.amazonaws.com/xj.min.css\">",
            style: styles.cssStyleSnippet.icon,
        },
        javascript: {
            code: "<script src=\"https://xj-cdn.s3-ap-southeast-1.amazonaws.com/xj.min.js\"></script><script>const xj = new XpressJobs('xj', recruiterId, {showlocations: true});</script>",
            style: styles.innerJavaScriptSnippet.icon,
        },
        html: {
            code: "<div id=\"xj\"></div>",
            style: styles.duplicateIcon,
        }
    };

    useEffect(() => {
        getDomainList();
    }, [])

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const name = event.target.name;
        const value = event.target.value;
        setDomaiDetail((values: any) => ({ ...values, [name]: value }));
        if(domainValidation[name] && domainValidation[name]!.invalid) {
            validateInput(name, value);
        }
    }

    const handleInputBlur = (event: React.FocusEvent<HTMLInputElement>) => {
        const name = event.target.name;
        const value = event.target.value;
        setDomaiDetail((values: any) => ({ ...values, [name]: value }));
        validateInput(name, value);
    }

    async function getDomainList() {
        setIsLoading(true);
        await getOrganizationDomains(recruiter.recruiterId, recruiter.token)
        .then((domains: WhitelistedDomain[]) => {
            setWhitelistedDomains(domains); 
            setIsLoading(false);
        })  
        .catch((error) => {            
            toErrorPage(error, UserType.Recruiter);
        });
    }

    async function addDomains(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        let validInputCount = Object.keys(domainValidation).filter(key => domainValidation[key]!.valid).length;
        if(validInputCount < 1) {
            validInputCount = validateDomainForm();
        }
        if(validInputCount == 1) {
            setIsLoading(true);
            await addOrganizationDomain(domainDetail, recruiter.token)
            .then((response: ResponseModel) => {
                openAlert(response.status ? "success" : "error", response.message);
                setWhitelistedDomains(response.data); 
                setDomaiDetail(initionalDomain);
            })
            .catch(() => {
                openAlert("error", "There was an error during the process!");
            })
            .finally(() => {
                setIsLoading(false);
            })
        }
        else {
            openAlert("error", "Domain can not be Empty!");            
        }
    }

    async function removeDomain(domainId: number) {
        const partnerToRemove = { ...domainDetail, ["id"]: domainId };
        setIsLoading(true);
        await removeOrganizationDomain(partnerToRemove, recruiter.token)
        .then((response: ResponseModel) => {
            openAlert(response.status ? "success" : "error", response.message);
            if(response.status){
                setWhitelistedDomains((prevList) => prevList.filter((p) => p.id !== domainId));
            }
        })
        .catch(() => {
            openAlert("error", "There was an error during the process!");
        })
        .finally(() => {
            setIsLoading(false);
        })
    }

    const copyToClipboard = async (event: React.MouseEvent, type: string) => {
        try {
            event.preventDefault();        
            event.stopPropagation();  
            let text = embedCodeConfig[type]?.code;
            if (!text) throw new Error("Invalid code type");

            if (type === "javascript" && recruiter?.recruiterId) {
                text = text.replace(/recruiterId/g, String(recruiter.recruiterId));
            }

            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(text);
            }   
            else {
                const textArea = document.createElement("textarea");
                textArea.value = text;
                textArea.style.position = "fixed";
                textArea.style.top = "-9999px";
                textArea.style.left = "-9999px";
                document.body.appendChild(textArea);
                textArea.focus();
                textArea.select();
                const success = document.execCommand("copy");
                document.body.removeChild(textArea);
                if (!success) throw new Error("Copy fallback failed");
            }
            openAlert("success", "Copied!");
        } 
        catch (err) {
            openAlert("error","Copy failed");
        }
    }

    const validateDomainForm = () => {
        let domainValidation: RecruiterDomainValidation = {
            domain: {valid: true}
        }
        if(domainDetail.domain === "") {
            domainValidation.domain = {valid: false, invalid: true, invalidMessage: "Domain is required!"};
        }
        setDomainValidation(domainValidation);
        let validInputCount = Object.keys(domainValidation).filter(key => domainValidation[key]!.valid).length;
        return validInputCount;
    }

    const validateInput = (name: string, value: string) => {
        if(name == "domain") {
            if(value == "") {
                setDomainValidation((values: any) => ({...values, [name]: {valid: false, invalid: true, invalidMessage: "Domain is required!"}}));
            } 
            else {
                setDomainValidation((values: any) => ({...values, [name]: {valid: true, invalid: false}}));
            }
        }
    }

    return(
        <>
            <div style={styles.mainWrapper}>
                <div style={styles.leftWrapper}>
                    <div style={styles.headerText}>{'Installation guide to put our jobs widget on your company careers page'}</div>
                    <div style={styles.subText}>{'Add the following HTML snippet to the place where you want to show the job list'}</div>
                    <div style={styles.jobListSnippet}>
                        {"<div"} 
                        <span style={styles.redText}>{"id="}</span> 
                        <span style={styles.greenText}>{"“xj”"}</span> 
                        {"></div>"}
                        <Duplicate 
                            style={styles.duplicateIcon} 
                            onClick={(e) =>{copyToClipboard(e, "html")}} 
                            onMouseEnter={() => setToolTipElement("html")} 
                            onMouseLeave={() => setToolTipElement(undefined)}
                        />
                        <span style={{...styles.dulicateMessage,...(toolTipElement == "html" && styles.dulicateMessage.jobListSnippet)}}>{"Copy"}</span>                        
                    </div>
                    <div style={styles.subText}>{'Add below JavaScript snippet'}</div>
                    <div style={styles.javaScriptSnippet}>
                        {"<script"} 
                        <span style={styles.redText}>{"src="}</span> 
                        <span style={styles.greenText}>{"“https://xj-cdn.s3-ap-southeast-1.amazonaws.com/xj.min.js”"}</span>{">"}
                        {"</script>"}
                        <br/>
                        <div style={styles.innerJavaScriptSnippet}>
                            {"<script"}
                            <br/>
                                <span style={styles.yellowTextWithMargin}>{"const"}</span> 
                                <span style={styles.purpleText}>{"xj"}</span>{" = "}
                                <span style={styles.yellowText}>{"new"}</span> 
                                <span style={styles.pinkText}>{"XpressJobs"}</span>
                                {"("}<span style={styles.greenText}>{"‘xj’"}</span>{","}
                                <span style={styles.greenText}>{recruiter.recruiterId}</span>{","}{"{"}
                                <span style={styles.blueText}>{"show "}</span> 
                                <span style={styles.blueTextWithMargin}>{" locations"}</span>
                                {':'} <span style={styles.purpleText}>{"true"}</span>{"});"}
                            <br/>     
                            {"</script>"} 
                            <span style={{...styles.dulicateMessage,...(toolTipElement == "javascript" && styles.dulicateMessage.javaScriptSnippet)}}>{"Copy"}</span>
                            <Duplicate 
                                style={styles.innerJavaScriptSnippet.icon} 
                                onClick={(e) => {copyToClipboard(e, "javascript")}} 
                                onMouseEnter={() => setToolTipElement("javascript")} 
                                onMouseLeave={() => setToolTipElement(undefined)}
                            />
                        </div>   
                    </div>
                    <div style={styles.subText}>{'Link CSS file to style the jobs list'}</div>
                    <div style={styles.cssStyleSnippet}>
                        {"<link "} 
                        <span style={styles.redText}>{"rel="}</span> 
                        <span style={styles.pinkText}>{"“stylesheet”"}</span> 
                        <span style={styles.redText}>{"href="}</span> 
                        <span style={styles.greenText}>{"“https://xj-cdn.s3-ap-"}</span>
                        <span style={styles.greenText}>{"southeast-1.amazonaws"}</span> 
                        <span style={styles.greenText}>{".com/xj.min.css”"}</span>{">"}
                        <span style={{...styles.dulicateMessage,...(toolTipElement == "css" && styles.dulicateMessage.cssStyleSnippet)}}>{"Copy"}</span>
                        <Duplicate 
                            style={styles.cssStyleSnippet.icon} 
                            onClick={(e) => {copyToClipboard(e, "css")}} 
                            onMouseEnter={() => setToolTipElement("css")} 
                            onMouseLeave={() => setToolTipElement(undefined)}
                        />
                    </div>
                    <div style={styles.additionalContent}>
                        <div style={styles.additionalHeader}>{'Additional Options'}</div>
                        {AdditionalOptions.map((option, index) => (
                            <div key={index} style={styles.option}>
                                <>
                                    <Button style={styles.optionButton}>{option.name}</Button>
                                    <div style={styles.defaultText}>{': default value -'}</div>
                                    <span style={styles.answerText}>{option.value}</span>
                                </>     
                            </div>
                        ))} 
                    </div>
                </div>
                <div style={styles.rightWrapper}>
                    <div style={styles.domainHeader}>{'Whitelisted Domains'}</div>
                    <div style={styles.inputLabel}>{'Add New Whitelisted Domain'}</div>
                    <TextInput
                        inputType={"text"}
                        name={"domain"}
                        value={domainDetail.domain}
                        styles={styles.textInput} 
                        handleChange={handleInputChange}
                        handleBlur={handleInputBlur}                            
                        validation={domainValidation.domain}                            
                    />
                    <Button style={styles.submitButton} onClick={addDomains}>{'ADD DOMAIN'}</Button>
                    <hr style={styles.line}/>
                    <div style={styles.inputLabel}>{'Domains'}</div>
                    {isLoading ? <IntegrationLoader styles={styles.loader}/> : 
                    <div style={styles.domainContent}>
                        {whitelistedDomains?.map((domain, index) => (
                            <div key={index} style={styles.domains}>{domain.domain}
                                <CloseIcon style={styles.closeIcon} onClick={() => removeDomain(domain.id)}/>
                            </div>
                        ))}
                    </div>}  
                </div>
            </div>
            <Alert/>
        </>
    )
}